import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ReferenceDot,
  Cell,
  LabelList
} from 'recharts';
import { useTheme } from '@material-ui/styles';
import { connect } from 'react-redux';
import selectors from 'store/selectors';
import { existingVsRecommendedIndex } from 'components/Settings/Palette/utils';
import { getComfortRanges } from './utils';
interface Props {
  ranges: any;
  markers: any;
  caseCustomer: any;
  riskSummaries: any;
  recommendedRiskAnalysis: any;
  existingOnly?: boolean;
}

const PolicyList: React.FC<{
  policies: any[];
  title: string;
}> = ({ policies, title }) => {
  const theme = useTheme() as any;

  return (
    <>
      {policies.length > 0 && (
        <div>
          <strong>{title}</strong>
          <ul style={{ listStyleType: 'none', padding: 0 }}>
            {policies.map((policy: any, index: number) => {
              const getBackgroundColor = () => {
                const charts = theme.palette.baseColors.charts;
                const comparisonColor = policy.name
                  .toLowerCase()
                  .includes('existing')
                  ? charts['comparisons'][
                      existingVsRecommendedIndex('existing')
                    ]
                  : policy.name.toLowerCase().includes('recommended')
                    ? charts['comparisons'][
                        existingVsRecommendedIndex('recommended')
                      ]
                    : null;

                return comparisonColor || charts['asset-allocation'][index];
              };

              return (
                <li
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '5px'
                  }}
                >
                  <div
                    style={{
                      width: '15px',
                      height: '15px',
                      backgroundColor: getBackgroundColor(), // Use the helper function
                      borderRadius: '50%',
                      marginRight: '10px'
                    }}
                  />
                  {`${policy.name}: ${Math.round(policy.growth_score)}`}
                  {policy.charge?.type === 'overall' && (
                    <span style={{ marginLeft: '5px', fontWeight: 'bold' }}>
                      (Existing)
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

const PolicyRiskAnalysisChart: React.FC<Props> = ({
  ranges,
  caseCustomer,
  riskSummaries,
  recommendedRiskAnalysis,
  existingOnly = false
}) => {
  const theme = useTheme() as any;
  const isData = ranges && ranges.length > 0;

  // Use policies to define markers
  const policies = existingOnly
    ? recommendedRiskAnalysis['policies']?.['existing']
    : [
        ...recommendedRiskAnalysis['policies']['existing'],
        ...recommendedRiskAnalysis['policies']['recommended']
      ];

  const markers = policies.map((policy: any) => {
    const getBackgroundColor = () => {
      const charts = theme.palette.baseColors.charts;
      const comparisonColor = policy.name.toLowerCase().includes('existing')
        ? charts['comparisons'][existingVsRecommendedIndex('existing')]
        : policy.name.toLowerCase().includes('recommended')
          ? charts['comparisons'][existingVsRecommendedIndex('recommended')]
          : null;

      return comparisonColor || charts['asset-allocation'][0]; // Fallback color
    };

    return {
      position: policy.growth_score,
      type: policy.name.toLowerCase().includes('overall') ? 'solid' : 'dotted',
      color: getBackgroundColor(),
      label: policy.name
    };
  });

  // Custom labels for the chart
  const customLabels = getComfortRanges(
    theme.palette.baseColors.charts['risk-analysis-ms']
  );

  const dataToRender = isData
    ? ranges.map((item: any) => {
        let cumulativeStart = 0;
        const processedItem: any = { name: item.name };
        const labelNames = customLabels.map(
          (label: any) => label?.key || label.label
        );

        Object.keys(item).forEach(key => {
          if (labelNames.includes(key)) {
            const value = item[key] as number;
            processedItem[key] = value;
            processedItem[`${key}_range`] =
              value && value > 5
                ? `${cumulativeStart === 0 ? 0 : cumulativeStart + 1} - ${cumulativeStart + value}`
                : null;
            cumulativeStart += value;
          }
        });

        return processedItem;
      })
    : [];

  const Triangle = () => (
    <svg width="10" height="10" viewBox="0 0 10 10">
      <polygon points="5,0 0,10 10,10" fill={theme.palette.primary.main} />
    </svg>
  );

  // Calculate arrow position based on markers
  const existingMarker = markers.find((marker: any) =>
    marker.label.toLowerCase().includes('existing')
  );
  const recommendedMarker = markers.find((marker: any) =>
    marker.label.toLowerCase().includes('recommended')
  );

  // Calculate arrow positions for existing and recommended markers
  const existingArrowPosition = existingMarker ? existingMarker.position : null;
  const recommendedArrowPosition = recommendedMarker
    ? recommendedMarker.position
    : null;

  // Arrow Indicator component
  const ArrowIndicator = ({ position, color }: any) =>
    position !== null ? (
      <div
        style={{
          position: 'absolute',
          top: `-15px`,
          left: `calc(${position}% - 18px)`,
          transform: 'rotate(180deg)',
          zIndex: 10
        }}
      >
        <svg width="20" height="10">
          <polygon points="10,0 20,10 0,10" fill={color} />
        </svg>
      </div>
    ) : (
      <></>
    );

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '20px'
        }}
      >
        <div style={{ textAlign: 'right', width: '150px', fontSize: '1rem' }}>
          <strong>{caseCustomer.display_name}</strong>
          <br />
          Risk tolerance: {riskSummaries[0]?.recommended_risk_profile}
        </div>

        <div style={{ flexGrow: 1 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '15px',
              width: '100%'
            }}
          >
            {customLabels.map((item: any, index: number) => {
              const rangeKey = `${item.key || item.label}_range`;
              const rangeValue =
                dataToRender.length > 0 ? dataToRender[0][rangeKey] : 'N/A';

              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    margin: '0 10px',
                    flexGrow: 1
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#6c757d',
                      marginBottom: '5px'
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      backgroundColor: item.color,
                      color: '#fff',
                      borderRadius: '5px',
                      padding: '5px 10px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {rangeValue || '-'}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ position: 'relative' }}>
            <ResponsiveContainer width="99%" height={200}>
              <BarChart
                layout="vertical"
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                data={dataToRender}
              >
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  axisLine={false}
                  tickLine={false}
                  ticks={Array.from({ length: 11 }, (_, index) => index * 10)}
                  tick={{ fontSize: 12 }}
                  interval={0}
                />

                <YAxis
                  type="category"
                  dataKey="name"
                  width={0}
                  tick={{ fontSize: '.9rem' }}
                  tickLine={false}
                  tickMargin={0}
                  hide={true}
                />

                {customLabels.map((label: any, index: number) => {
                  const key = label?.key || label.label;
                  return (
                    <Bar
                      key={index}
                      dataKey={key}
                      stackId="a"
                      fill={label.color}
                    >
                      {dataToRender.map((entry: any, idx: number) => {
                        // Reinitialize radius and maxKey for each cell
                        let radius = 0;
                        let maxKey = '';
                        customLabels.forEach((lbl: any) => {
                          const barKey = lbl?.key || lbl.label;
                          if (entry[barKey] !== null) {
                            maxKey = barKey;
                          }
                        });
                        if (maxKey === key) {
                          radius = 25;
                        }
                        return (
                          <Cell
                            key={`cell-${idx}`}
                            // @ts-ignore
                            radius={[0, radius, radius, 0]}
                          />
                        );
                      })}
                      <LabelList
                        dataKey={`${key}_range`}
                        position="inside"
                        fill="#fff"
                        fontSize={14}
                        fontWeight="bold"
                      />
                    </Bar>
                  );
                })}

                <ReferenceLine
                  x={0}
                  stroke={theme.palette.primary.main}
                  strokeWidth={4}
                />

                <ReferenceDot
                  x={0}
                  y={0.5}
                  isFront={true}
                  shape={<Triangle />}
                />

                {ranges.map((range: any, index: number) => (
                  <ReferenceLine
                    key={index}
                    x1={range.start}
                    x2={range.end}
                    stroke={range.color}
                    strokeWidth={12}
                    strokeOpacity={0.3}
                  />
                ))}

                {markers.map((marker: any, index: number) => (
                  <ReferenceLine
                    key={index}
                    x={marker.position}
                    stroke={marker.color}
                    strokeWidth={5}
                    strokeDasharray={
                      marker.type === 'dotted' ? '5 5' : undefined
                    }
                    label={
                      marker.label
                        ? {
                            position: 'bottom',
                            fill: marker.color,
                            fontSize: 12
                          }
                        : undefined
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>

            {/* Arrow Indicator for Existing */}
            <ArrowIndicator
              position={existingArrowPosition}
              color={
                theme.palette.baseColors.charts['comparisons'][
                  existingVsRecommendedIndex('existing')
                ]
              }
            />

            {/* Arrow Indicator for Recommended */}
            <ArrowIndicator
              position={recommendedArrowPosition}
              color={
                theme.palette.baseColors.charts['comparisons'][
                  existingVsRecommendedIndex('recommended')
                ]
              }
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        <div style={{ flex: '1 1 0%', display: 'flex', gap: '5rem' }}>
          {existingOnly ? (
            <PolicyList
              policies={recommendedRiskAnalysis['policies']['existing']}
              title="Existing Policies"
            />
          ) : (
            <>
              <PolicyList
                policies={recommendedRiskAnalysis['policies']['existing']}
                title="Existing Policies"
              />
              {recommendedRiskAnalysis['policies']['recommended'].length >
                0 && (
                <PolicyList
                  policies={recommendedRiskAnalysis['policies']['recommended']}
                  title="Recommended Policies"
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default connect((state: any) => ({
  existingPolicies: selectors.caseSelectors.existingPolicies(state),
  recommendedRiskAnalysis:
    selectors.caseSelectors.recommendedRiskAnalysis(state),
  caseCustomer: selectors.caseSelectors.caseCustomer(state),
  riskSummaries: selectors.caseSelectors.getRiskSummary(state)
}))(PolicyRiskAnalysisChart);
